/**
 * Static content shipped in the first load: economy table, catalog and remote-config defaults.
 * Level JSON is NOT here; it is lazy-loaded per level (services/levels.ts).
 */
import economyJson from '@mp/content/economy/economy.v1.json';
import shopsJson from '@mp/content/catalog/shops.json';
import itemsJson from '@mp/content/catalog/items.json';
import citiesJson from '@mp/content/catalog/cities.json';
import remoteJson from '@mp/content/remote-config/defaults.json';
import rareJson from '@mp/content/catalog/rare-items.json';

export type BoosterKey = 'undo' | 'shuffle' | 'magnet' | 'extraSlot';
export const BOOSTERS: readonly BoosterKey[] = ['undo', 'shuffle', 'magnet', 'extraSlot'];

export interface Grant {
  coins?: number;
  gems?: number;
  boosters?: Partial<Record<BoosterKey, number>>;
}

export interface Economy {
  version: number;
  start: { coins: number; gems: number; boosters: Record<BoosterKey, number> };
  xp: { win: number; fail: number; threeStarBonus: number; table: number[] };
  featureUnlocks: Record<string, number>;
  round: {
    rescueThreshold: number;
    maxRescues: number;
    coinsOrderPop: number;
    coinsClutterPop: number;
    coinsSweep: number;
    rescueTimeMs: number;
  };
  /** coins = baseCoins + perTenLevels × ⌊min(n, capLevel) / 10⌋: rewards stop growing at capLevel (L600) */
  levelReward: { baseCoins: number; perTenLevels: number; capLevel: number };
  rescue: { gems: number; firstFree: boolean };
  boosterPrices: Record<BoosterKey, number>;
  offlineSales: { capHours: number; pctPerUpgrade: number; minPopupCoins: number };
  dailyGift: Grant[];
  streakMilestones: Record<string, Grant>;
  ads: {
    /** per slot: its daily cap; the 2L slots that pay at completion also carry what they grant */
    rewarded: Record<string, { cap: number }> & { freeGems: { cap: number; gems: number }; booster: { cap: number; qty: number } };
    interstitial: { minLevel: number; everyRounds: number; minGapSec: number; afterRewardedGapSec: number; afterFail: boolean };
  };
  dailySpin: Array<Grant & { w: number }>;
  league: { groupSize: number; tiers: string[]; rankRewards: Grant[]; promotedReward: Grant; tierMultiplierPct: number };
  restock: { perDay: number; linkHours: number; helperCooldownHours: number; helperGift: Grant; senderReward: Grant };
  daily: { reward: Grant; shareSticker: boolean };
  beat: { trophyDays: number; reward: Grant };
  store: { products: StoreProductDef[]; dailySpendConfirmUsdCents?: number };
  costumes: string[];
  /** Pip looks in wardrobe order, `classic` (kraft) first */
  skins: string[];
}

export interface StoreProductDef {
  id: string;
  kind: string;
  gems?: number;
  coins?: number;
  boosters?: Partial<Record<BoosterKey, number>>;
  priceUsdCents?: number;
  priceGems?: number;
  label?: string;
  oneTime?: boolean;
  offerHours?: number;
  costume?: string;
  skin?: string;
}

export interface RareItemDef {
  id: string;
  shop: string;
  name: string;
  gemPrice: number;
}

export interface UpgradeDef {
  slot: string;
  id: string;
  name: string;
  coins: number;
  starsGate: number;
  styles: string[];
  /** SO-01: this slot also needs (and consumes) one rare item */
  rareItem?: string;
}

export interface ShopDef {
  id: string;
  city: string;
  name: string;
  order: number;
  levels: [number, number];
  baseRatePerHour: number;
  unlockStars: number;
  upgrades: UpgradeDef[];
}

export interface ItemDef {
  id: string;
  shop: string;
  city: string;
  name: string;
}

export interface CityDef {
  id: string;
  name: string;
  order: number;
  shops: string[];
}

/** One A/B test of remote config: a variant of weight 0 takes no one (an arm parked until its knob is wired). */
export interface ExperimentDef {
  salt: string;
  variants: { name: string; weight: number; set: Record<string, unknown> }[];
}

export interface RemoteConfig {
  version: number;
  flags: Record<string, boolean>;
  economyVersion: number;
  anticheat: { replaySampleRate: number; minTapGapMs: number; topN: number };
  rescueThreshold: number;
  levelPack: Record<string, number>;
  /** the tests that run (GET /v1/config sends the definitions with the player's own picks in `assignments`) */
  experiments: Record<string, ExperimentDef>;
  /** experiment name → the variant this player is in; sent only to a signed-in player */
  assignments?: Record<string, string>;
  /** MO-02 interstitial rules over economy.ads.interstitial: any subset (the first-interstitial test sets `minLevel`) */
  interstitial?: Partial<Economy['ads']['interstitial']>;
  /** the restock share copy (share.restock.a|b|c) an experiment assigns; unset: the stable per-player pick */
  restockShare?: 'a' | 'b' | 'c';
}

export const economy = economyJson as unknown as Economy;
export const shops = (shopsJson as { shops: ShopDef[] }).shops;
export const items = (itemsJson as { items: ItemDef[] }).items;
export const cities = (citiesJson as { cities: CityDef[] }).cities;
export const remoteDefaults = remoteJson as unknown as RemoteConfig;
export const rareItems = (rareJson as { rareItems: RareItemDef[] }).rareItems;
const rareById = new Map(rareItems.map((r) => [r.id, r]));

export function rareItemDef(id: string): RareItemDef | undefined {
  return rareById.get(id);
}

const shopById = new Map(shops.map((s) => [s.id, s]));
const itemById = new Map(items.map((i) => [i.id, i]));

export function shopDef(id: string): ShopDef | undefined {
  return shopById.get(id);
}

export function itemDef(id: string): ItemDef | undefined {
  return itemById.get(id);
}

export function cityShops(city: string): ShopDef[] {
  return shops.filter((s) => s.city === city).sort((a, b) => a.order - b.order);
}

/** Which shop owns a level number (1-based, global within the city list). */
export function shopForLevel(city: string, n: number): ShopDef | undefined {
  return cityShops(city).find((s) => n >= s.levels[0] && n <= s.levels[1]);
}

export function itemsForShop(shop: string): ItemDef[] {
  return items.filter((i) => i.shop === shop);
}

/** A city pack's catalog (content/cities/<id>/catalog.json, LO-02). */
export interface CityCatalog {
  version?: number;
  city: CityDef;
  shops: ShopDef[];
  items: ItemDef[];
  rareItems?: RareItemDef[];
}

function upsert<T extends { id: string }>(list: T[], byId: Map<string, T> | null, add: readonly T[]): void {
  for (const x of add) {
    const i = list.findIndex((y) => y.id === x.id);
    if (i >= 0) list[i] = x;
    else list.push(x);
    byId?.set(x.id, x);
  }
}

/**
 * LO-02 extension point: add a downloaded city's catalog (city, shops, items, rare items) to
 * the static content, so cityShops(), shopDef() and itemDef() know it. Idempotent per id.
 */
export function registerCityCatalog(cat: CityCatalog): CityDef {
  upsert(cities, null, [cat.city]);
  cities.sort((a, b) => a.order - b.order);
  upsert(shops, shopById, cat.shops);
  upsert(items, itemById, cat.items);
  upsert(rareItems, rareById, cat.rareItems ?? []);
  return cat.city;
}
